import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Button,
    Chip,
    Paper,
    Grid,
    IconButton,
    Alert,
    InputAdornment,
    FormLabel,
    FormHelperText,
    Container,
    Autocomplete,
    CircularProgress,
    Snackbar
} from "@mui/material";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";

import { createNewJob, getActiveLocations } from "../../Api/JobsServiceApi";
import { GetBenifits, searchBenefitsApi } from "../../Api/Benifitsapi";
import { getAllSkills, searchSkillsApi } from "../../Api/SkillsApi";

const BRAND = '#6B31C4';
const BRAND_DARK = '#5a29a5';

const formatIndianNumber = (val) => {
    if (!val) return '';
    const cleanVal = val.toString().replace(/\D/g, '');
    if (cleanVal === '') return '';
    
    const lastThree = cleanVal.substring(cleanVal.length - 3);
    const otherNumbers = cleanVal.substring(0, cleanVal.length - 3);
    if (otherNumbers !== '') {
        const formattedOthers = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
        return `${formattedOthers},${lastThree}`;
    }
    return lastThree;
};

const initialFormState = {
    title: "",
    city: "",
    area: "",
    pinCode: "",
    recruitmentTimeline: "",
    positions: "",
    jobType: "",
    experience: "",
    workType: "",
    payMaximum: "",
    payPeriod: "",
    jobEndDate: "",
    description: "",
};

const mapEmploymentType = (type) => {
    switch (type) {
        case "Full Time": return "full_time";
        case "Part Time": return "part_time";
        case "Contract":  return "contract";
        case "Internship": return "internship";
        default: return "full_time";
    }
};

const mapWorkType = (type) => {
    switch (type) {
        case "Work From Office": return "work_from_office";
        case "Remote":           return "remote";
        case "Hybrid":           return "hybrid";
        default: return "work_from_office";
    }
};

const mapLocationType = (type) => {
    switch (type) {
        case "Work From Office": return "onsite";
        case "Remote":           return "remote";
        case "Hybrid":           return "hybrid";
        default: return "onsite";
    }
};

const mapSalaryPeriod = (period) => {
    switch (period) {
        case "Per Hour":  return "per_hour";
        case "Per Day":   return "per_week"; // mapping day to week fallback
        case "Per Month": return "per_month";
        case "Per Year":  return "per_annum";
        default: return "per_month";
    }
};

const CreateNewJob = () => {
    const [formData, setFormData] = useState(initialFormState);
    
    // Loaded lists from backend
    const [cities, setCities] = useState([]);
    const [allBenefits, setAllBenefits] = useState([]);
    const [allSkills, setAllSkills] = useState([]);
    
    // User selections (contain full objects { id, name })
    const [selectedBenefits, setSelectedBenefits] = useState([]);
    const [selectedSkills, setSelectedSkills] = useState([]);
    const [visibleSkillsLimit, setVisibleSkillsLimit] = useState(15);
    
    // Autocomplete states
    const [skillsInputValue, setSkillsInputValue] = useState("");
    const [benefitsInputValue, setBenefitsInputValue] = useState("");
    const [loadingSkills, setLoadingSkills] = useState(false);
    const [loadingBenefits, setLoadingBenefits] = useState(false);

    const [loading, setLoading] = useState(true);
    const [editorRef, setEditorRef] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [errors, setErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState("");
    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

    // Debounced search for skills
    useEffect(() => {
        setVisibleSkillsLimit(15);
        const handler = setTimeout(async () => {
            if (skillsInputValue.trim() !== '') {
                setLoadingSkills(true);
                try {
                    const res = await searchSkillsApi(skillsInputValue);
                    if (res?.success) {
                        setAllSkills(res.data || []);
                    }
                } catch (error) {
                    console.error("Failed to search skills", error);
                } finally {
                    setLoadingSkills(false);
                }
            } else {
                // Fetch default if empty, or just leave current options
                try {
                    const res = await getAllSkills(1000);
                    if (res?.success) {
                        setAllSkills(res.data || []);
                    }
                } catch (error) {
                    setAllSkills([]);
                }
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [skillsInputValue]);

    // Debounced search for benefits
    useEffect(() => {
        const handler = setTimeout(async () => {
            if (benefitsInputValue.trim() !== '') {
                setLoadingBenefits(true);
                try {
                    const res = await searchBenefitsApi(benefitsInputValue);
                    if (res?.success) {
                        setAllBenefits(res.data || []);
                    }
                } catch (error) {
                    console.error("Failed to search benefits", error);
                } finally {
                    setLoadingBenefits(false);
                }
            } else {
                // Restore initial benefits when search input is empty
                try {
                    const benefitsRes = await GetBenifits();
                    const rawBenefits = Array.isArray(benefitsRes) ? benefitsRes : benefitsRes?.data || [];
                    setAllBenefits(rawBenefits.map(b => typeof b === 'string' ? { id: b, name: b } : b));
                } catch (error) {
                    setAllBenefits([]);
                }
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [benefitsInputValue]);

    const applyFormat = (command, value = null) => {
        document.execCommand(command, false, value);
    };
    const handleBold = () => applyFormat("bold");
    const handleItalic = () => applyFormat("italic");
    const handleUnderline = () => applyFormat("underline");
    const handleStrikethrough = () => applyFormat("strikeThrough");
    const handleBulletList = () => applyFormat("insertUnorderedList");
    const handleNumberList = () => applyFormat("insertOrderedList");
    const handleSubscript = () => applyFormat("subscript");
    const handleSuperscript = () => applyFormat("superscript");
    const handleAlignLeft = () => applyFormat("justifyLeft");
    const handleAlignCenter = () => applyFormat("justifyCenter");
    const handleAlignRight = () => applyFormat("justifyRight");

    const handleDescriptionChange = (event) => {
        setFormData(prev => ({ ...prev, description: event.target.innerHTML }));
        if (errors.description) {
            setErrors(prev => {
                const copy = { ...prev };
                delete copy.description;
                return copy;
            });
        }
    };

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                // 1. Fetch active locations/cities
                const citiesRes = await getActiveLocations();
                if (citiesRes?.success) {
                    setCities(citiesRes.locations || []);
                }
                
                // 2. Fetch active benefits
                const benefitsRes = await GetBenifits();
                const rawBenefits = Array.isArray(benefitsRes) ? benefitsRes : benefitsRes?.data || [];
                setAllBenefits(rawBenefits.map(b => typeof b === 'string' ? { id: b, name: b } : b));

                // 3. Fetch skills
                const skillsRes = await getAllSkills(1000);
                if (skillsRes?.success) {
                    setAllSkills(skillsRes.data || []);
                }
            } catch (error) {
                console.error("Failed to load initial form data:", error);
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();
    }, []);

    const handleInputChange = (field) => (event) => {
        const value = event.target.value;
        if (errors[field]) {
            setErrors(prev => {
                const copy = { ...prev };
                delete copy[field];
                return copy;
            });
        }
        if (field === "pinCode") {
            if (value === "" || (/^\d+$/.test(value) && value.length <= 6)) {
                setFormData({ ...formData, pinCode: value });
            }
            return;
        }
        if (field === "payMaximum") {
            const rawDigits = value.replace(/,/g, '');
            if (rawDigits === '' || /^\d+$/.test(rawDigits)) {
                setFormData({ ...formData, payMaximum: formatIndianNumber(rawDigits) });
            }
            return;
        }
        setFormData({ ...formData, [field]: value });
    };

    const handleBenefitToggle = (benefit) => {
        setSelectedBenefits(prev => {
            const exists = prev.some(b => b.id === benefit.id);
            if (exists) {
                return prev.filter(b => b.id !== benefit.id);
            } else {
                return [...prev, benefit];
            }
        });
    };

    const handleSkillToggle = (skill) => {
        setSelectedSkills(prev => {
            const exists = prev.some(s => s.id === skill.id);
            const nextList = exists ? prev.filter(s => s.id !== skill.id) : [...prev, skill];
            
            if (nextList.length > 0 && errors.skills) {
                setErrors(e => {
                    const copy = { ...e };
                    delete copy.skills;
                    return copy;
                });
            }
            return nextList;
        });
    };

    const scrollToField = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            const input = element.querySelector('input') || element.querySelector('select') || element;
            if (input && typeof input.focus === 'function') {
                setTimeout(() => {
                    input.focus({ preventScroll: true });
                }, 500);
            }
        }
    };

    const handleSubmit = async () => {
        setSubmitError("");
        const newErrors = {};

        const titleTrimmed = formData.title ? formData.title.trim() : "";
        if (!titleTrimmed) {
            newErrors.title = "Job Title is required.";
        } else if (titleTrimmed.length < 3 || titleTrimmed.length > 100) {
            newErrors.title = "Job Title must be between 3 and 100 characters.";
        } else if (!/[a-zA-Z0-9]/.test(titleTrimmed)) {
            newErrors.title = "Job Title must contain letters or numbers.";
        } else {
            const titlePattern = /^[a-zA-Z0-9\s\-\/\(\)\.\,]+$/;
            if (!titlePattern.test(titleTrimmed)) {
                newErrors.title = "Job Title contains invalid characters. Only letters, numbers, spaces, hyphens (-), slashes (/), parentheses (), periods (.), and commas (,) are allowed.";
            }
        }

        const areaTrimmed = formData.area ? formData.area.trim() : "";
        if (areaTrimmed) {
            if (areaTrimmed.length < 2 || areaTrimmed.length > 100) {
                newErrors.area = "Area must be between 2 and 100 characters.";
            } else {
                const areaPattern = /^[a-zA-Z0-9\s,\-./()]+$/;
                if (!areaPattern.test(areaTrimmed)) {
                    newErrors.area = "Area contains invalid characters. Only letters, numbers, spaces, and standard punctuation are allowed.";
                }
            }
        }

        if (!formData.city) {
            newErrors.city = "City is required.";
        }
        if (!formData.recruitmentTimeline) {
            newErrors.recruitmentTimeline = "Recruitment timeline is required.";
        }
        if (!formData.jobType) {
            newErrors.jobType = "Job Type is required.";
        }
        if (!formData.experience) {
            newErrors.experience = "Experience Level is required.";
        }
        if (!formData.workType) {
            newErrors.workType = "Work Type is required.";
        }
        if (!formData.payMaximum) {
            newErrors.payMaximum = "Pay Maximum is required.";
        }
        if (!formData.payPeriod) {
            newErrors.payPeriod = "Pay Period is required.";
        }
        if (!formData.positions) {
            newErrors.positions = "Number of people to hire is required.";
        }
        if (!formData.jobEndDate) {
            newErrors.jobEndDate = "Job End Date is required.";
        }
        if (selectedSkills.length === 0) {
            newErrors.skills = "At least one skill is required.";
        }
        if (!formData.description) {
            newErrors.description = "Job Description is required.";
        }

        if (formData.pinCode && formData.pinCode.trim().length !== 6) {
            newErrors.pinCode = "Please enter a valid 6-digit pin code.";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            if (newErrors.title) scrollToField("job-title-input");
            else if (newErrors.city) scrollToField("city-select");
            else if (newErrors.area) scrollToField("area-input");
            else if (newErrors.pinCode) scrollToField("pincode-input");
            else if (newErrors.recruitmentTimeline) scrollToField("recruitment-timeline-select");
            else if (newErrors.jobType) scrollToField("job-type-select");
            else if (newErrors.experience) scrollToField("experience-level-select");
            else if (newErrors.workType) scrollToField("work-type-select");
            else if (newErrors.payMaximum) scrollToField("pay-maximum-input");
            else if (newErrors.payPeriod) scrollToField("pay-period-select");
            else if (newErrors.positions) scrollToField("positions-input");
            else if (newErrors.jobEndDate) scrollToField("job-end-date-input");
            else if (newErrors.skills) scrollToField("skills-input");
            else if (newErrors.description) scrollToField("job-description-editor");
            return;
        }

        setErrors({});
        setIsSubmitting(true);
        setSubmitError("");
        setSubmitSuccess("");

        const selectedCityObj = cities.find(c => c.name === formData.city);

        const rawPayMax = typeof formData.payMaximum === 'string'
            ? formData.payMaximum.replace(/,/g, '')
            : formData.payMaximum;

        const payload = {
            title:               titleTrimmed,
            description:         formData.description,
            locationId:          selectedCityObj ? selectedCityObj.id : null,
            recruitmentTimeline: formData.recruitmentTimeline,
            jobEndDate:          formData.jobEndDate ? new Date(formData.jobEndDate).toISOString() : null,
            shortDescription:    titleTrimmed, // backend fallback
            employmentType:      mapEmploymentType(formData.jobType),
            positions:           Number(formData.positions),
            minSalary:           Number(rawPayMax),
            maxSalary:           Number(rawPayMax),
            salaryPeriod:        mapSalaryPeriod(formData.payPeriod),
            currency:            "INR",
            experience:          formData.experience,
            workType:            mapWorkType(formData.workType),
            benefits:            selectedBenefits.map(b => ({ benefitId: b.id, name: b.name })),
            skills:              selectedSkills.map(s => ({ skillId: s.id, importance: 1, name: s.name || s.skillName })),
            locations: [{
                locationType: mapLocationType(formData.workType),
                city:         formData.city,
                area:         areaTrimmed || null,
                pincode:      formData.pinCode || null,
                isPrimary:    true
            }]
        };

        try {
            await createNewJob(payload);
            setSubmitSuccess("Job posted successfully.");
            setSnack({ open: true, message: "Job posted successfully!", severity: "success" });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setFormData(initialFormState);
            setSelectedBenefits([]);
            setSelectedSkills([]);
            if (editorRef) {
                editorRef.innerHTML = "";
            }
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || "Failed to post job. Please check details and try again.";
            setSubmitError(errorMsg);
            setSnack({ open: true, message: errorMsg, severity: "error" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputLabelStyle = { fontSize: '0.8125rem', fontWeight: 500, color: '#374151', mb: 0.75, display: 'block' };

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, backgroundColor: '#F8F9FA', minHeight: '100vh' }}>
            <Container maxWidth="lg" sx={{ px: 0 }}>
                {/* Title */}
                <Typography variant="h3" sx={{ mb: 3, fontWeight: 700 }}>
                    Create a New Job
                </Typography>

                <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3, md: 4 }, border: '1px solid #E5E7EB', borderRadius: '12px', backgroundColor: '#FFFFFF' }}>
                    
                    {/* Job Title */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 12, md: 6, lg: 6 }}>
                            <FormLabel sx={inputLabelStyle}>Job Title *</FormLabel>
                            <TextField
                                id="job-title-input"
                                error={!!errors.title}
                                fullWidth
                                size="small"
                                placeholder="Enter job title (e.g. Senior Frontend Engineer)"
                                value={formData.title}
                                onChange={handleInputChange("title")}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <FormHelperText error={!!errors.title} sx={{ mt: 0.5 }}>
                                {errors.title ? errors.title : "Use a clear, descriptive title to attract the right candidates."}
                            </FormHelperText>
                        </Grid>
                    </Grid>

                    {/* Location Details */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A2E', mb: 2, borderBottom: '1px solid #F3F4F6', pb: 1, fontSize: '0.9375rem' }}>
                        Location Details
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                            <FormLabel sx={inputLabelStyle}>City *</FormLabel>
                            <FormControl id="city-select" error={!!errors.city} fullWidth size="small">
                                <Select
                                    value={formData.city}
                                    onChange={handleInputChange("city")}
                                    displayEmpty
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">Select city</MenuItem>
                                    {cities.map(c => (
                                        <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText sx={{ mt: 0.5 }}>
                                    {errors.city ? errors.city : "The primary city where the employee will be based."}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                            <FormLabel sx={inputLabelStyle}>Area</FormLabel>
                            <TextField
                                id="area-input"
                                error={!!errors.area}
                                fullWidth
                                size="small"
                                placeholder="Enter area (e.g. Gachibowli, Koramangala)"
                                value={formData.area}
                                onChange={handleInputChange("area")}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <FormHelperText error={!!errors.area} sx={{ mt: 0.5 }}>
                                {errors.area ? errors.area : "Optional neighborhood or office area within the city."}
                            </FormHelperText>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                            <FormLabel sx={inputLabelStyle}>Pin code</FormLabel>
                            <TextField
                                id="pincode-input"
                                error={!!errors.pinCode}
                                fullWidth
                                size="small"
                                placeholder="Enter 6-digit pin code"
                                value={formData.pinCode}
                                onChange={handleInputChange("pinCode")}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <FormHelperText error={!!errors.pinCode} sx={{ mt: 0.5 }}>
                                {errors.pinCode ? errors.pinCode : "6-digit postal code of the office location."}
                            </FormHelperText>
                        </Grid>
                    </Grid>

                    {/* Job Details */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1A1A2E', mb: 2, borderBottom: '1px solid #F3F4F6', pb: 1, fontSize: '0.9375rem' }}>
                        Job Details
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 4 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Recruitment timeline for this job? *</FormLabel>
                            <FormControl id="recruitment-timeline-select" error={!!errors.recruitmentTimeline} fullWidth size="small">
                                <Select
                                    value={formData.recruitmentTimeline}
                                    onChange={handleInputChange("recruitmentTimeline")}
                                    displayEmpty
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">Select timeline</MenuItem>
                                    <MenuItem value="Immediate (1-3 days)">Immediate (1-3 days)</MenuItem>
                                    <MenuItem value="Very Urgent (3-7 days)">Very Urgent (3-7 days)</MenuItem>
                                    <MenuItem value="Urgent (1-2 weeks)">Urgent (1-2 weeks)</MenuItem>
                                    <MenuItem value="Normal (3-4 weeks)">Normal (3-4 weeks)</MenuItem>
                                    <MenuItem value="Flexible (1-2 months)">Flexible (1-2 months)</MenuItem>
                                    <MenuItem value="Ongoing (No strict deadline)">Ongoing (No strict deadline)</MenuItem>
                                </Select>
                                <FormHelperText sx={{ mt: 0.5 }}>
                                    {errors.recruitmentTimeline ? errors.recruitmentTimeline : "Helps prioritize candidate screening timelines."}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Job Type *</FormLabel>
                            <FormControl id="job-type-select" error={!!errors.jobType} fullWidth size="small">
                                <Select
                                    value={formData.jobType}
                                    onChange={handleInputChange("jobType")}
                                    displayEmpty
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">Select Job Type</MenuItem>
                                    <MenuItem value="Full Time">Full Time</MenuItem>
                                    <MenuItem value="Part Time">Part Time</MenuItem>
                                    <MenuItem value="Contract">Contract</MenuItem>
                                    <MenuItem value="Internship">Internship</MenuItem>
                                    <MenuItem value="Freelance">Freelance</MenuItem>
                                    <MenuItem value="Temporary">Temporary</MenuItem>
                                </Select>
                                <FormHelperText sx={{ mt: 0.5 }}>
                                    {errors.jobType ? errors.jobType : "Contractual nature of the vacancy."}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Experience Level *</FormLabel>
                            <FormControl id="experience-level-select" error={!!errors.experience} fullWidth size="small">
                                <Select
                                    value={formData.experience}
                                    onChange={handleInputChange("experience")}
                                    displayEmpty
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">Select experience level</MenuItem>
                                    <MenuItem value="No Experience">Internship / No Experience</MenuItem>
                                    <MenuItem value="Fresher">Fresher / Entry Level (0-1 Year)</MenuItem>
                                    <MenuItem value="1-2 Years">Junior (1-2 Years)</MenuItem>
                                    <MenuItem value="2-3 Years">Associate (2-3 Years)</MenuItem>
                                    <MenuItem value="3-5 Years">Mid-level (3-5 Years)</MenuItem>
                                    <MenuItem value="5-8 Years">Senior (5-8 Years)</MenuItem>
                                    <MenuItem value="8-10 Years">Lead / Principal (8-10 Years)</MenuItem>
                                    <MenuItem value="10+ Years">Manager / Director (10+ Years)</MenuItem>
                                    <MenuItem value="15+ Years">Executive / VP (15+ Years)</MenuItem>
                                </Select>
                                <FormHelperText sx={{ mt: 0.5 }}>
                                    {errors.experience ? errors.experience : "Required experience level of the candidate."}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Work Type *</FormLabel>
                            <FormControl id="work-type-select" error={!!errors.workType} fullWidth size="small">
                                <Select
                                    value={formData.workType}
                                    onChange={handleInputChange("workType")}
                                    displayEmpty
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">Select work type</MenuItem>
                                    <MenuItem value="Work From Office">Work From Office (On-site)</MenuItem>
                                    <MenuItem value="Remote">Remote (Work From Home)</MenuItem>
                                    <MenuItem value="Hybrid">Hybrid (Split / On-site + Remote)</MenuItem>
                                </Select>
                                <FormHelperText sx={{ mt: 0.5 }}>
                                    {errors.workType ? errors.workType : "The workspace configuration policy."}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Pay Maximum *</FormLabel>
                            <TextField
                                id="pay-maximum-input"
                                error={!!errors.payMaximum}
                                fullWidth
                                size="small"
                                type="text"
                                placeholder="e.g. 5,00,000"
                                value={formData.payMaximum}
                                onChange={handleInputChange("payMaximum")}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                inputProps={{ inputMode: 'numeric', pattern: '[0-9,]*' }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <span style={{ fontWeight: 600, color: '#475569' }}>₹</span>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormHelperText error={!!errors.payMaximum} sx={{ mt: 0.5 }}>
                                {errors.payMaximum ? errors.payMaximum : "Maximum salary budget allocated in INR (₹)."}
                            </FormHelperText>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Pay *</FormLabel>
                            <FormControl id="pay-period-select" error={!!errors.payPeriod} fullWidth size="small">
                                <Select
                                    value={formData.payPeriod}
                                    onChange={handleInputChange("payPeriod")}
                                    displayEmpty
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="">Select period</MenuItem>
                                    <MenuItem value="Per Hour">Per Hour</MenuItem>
                                    <MenuItem value="Per Day">Per Day</MenuItem>
                                    <MenuItem value="Per Month">Per Month</MenuItem>
                                    <MenuItem value="Per Year">Per Year</MenuItem>
                                </Select>
                                <FormHelperText sx={{ mt: 0.5 }}>
                                    {errors.payPeriod ? errors.payPeriod : "Billing period for the salary amount."}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Number of people to hire *</FormLabel>
                            <TextField
                                id="positions-input"
                                error={!!errors.positions}
                                fullWidth
                                size="small"
                                type="number"
                                placeholder="Enter number of vacancies"
                                value={formData.positions}
                                onChange={handleInputChange("positions")}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <FormHelperText error={!!errors.positions} sx={{ mt: 0.5 }}>
                                {errors.positions ? errors.positions : "Total positions open for recruitment."}
                            </FormHelperText>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3, lg: 3 }}>
                            <FormLabel sx={inputLabelStyle}>Job End Date *</FormLabel>
                            <TextField
                                id="job-end-date-input"
                                error={!!errors.jobEndDate}
                                fullWidth
                                size="small"
                                type="date"
                                placeholder="dd-mm-yyyy"
                                value={formData.jobEndDate}
                                onChange={handleInputChange("jobEndDate")}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: new Date().toISOString().split('T')[0] }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            />
                            <FormHelperText error={!!errors.jobEndDate} sx={{ mt: 0.5 }}>
                                {errors.jobEndDate ? errors.jobEndDate : "Deadline to accept job applications."}
                            </FormHelperText>
                        </Grid>
                    </Grid>

                    {/* Benefits */}
                    <Box sx={{ mb: 4 }}>
                        <FormLabel sx={inputLabelStyle}>Benefits</FormLabel>
                        <TextField
                            size="small"
                            placeholder="Search and add benefits..."
                            value={benefitsInputValue}
                            onChange={(e) => setBenefitsInputValue(e.target.value)}
                            sx={{ mt: 1, mb: 2, width: '100%', maxWidth: '500px', display: 'block', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                                endAdornment: loadingBenefits ? <CircularProgress size={20} /> : null
                            }}
                        />

                        {/* Selected Benefits */}
                        {selectedBenefits.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ display: 'block', color: '#6B31C4', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Selected Benefits ({selectedBenefits.length})
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {selectedBenefits.map((benefit) => (
                                        <Chip
                                            key={`selected-${benefit.id}`}
                                            label={benefit.name}
                                            onDelete={() => handleBenefitToggle(benefit)}
                                            deleteIcon={<ClearIcon sx={{ fontSize: '14px !important', color: 'white' }} />}
                                            sx={{
                                                fontSize: "0.82rem",
                                                py: 1.8,
                                                px: 0.5,
                                                borderRadius: '8px',
                                                backgroundColor: BRAND,
                                                color: 'white',
                                                fontWeight: 600,
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    '&:hover': {
                                                        color: 'white'
                                                    }
                                                },
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: BRAND_DARK,
                                                    transform: 'translateY(-1px)',
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Suggested/Search Benefits */}
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#888', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {benefitsInputValue.trim() !== '' ? 'Search Results' : 'Suggested Benefits'}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {allBenefits
                                    .filter(b => !selectedBenefits.some(sb => sb.id === b.id))
                                    .slice(0, 15)
                                    .map((benefit) => (
                                        <Chip
                                            key={benefit.id}
                                            label={benefit.name}
                                            onClick={() => handleBenefitToggle(benefit)}
                                            variant="outlined"
                                            icon={<span style={{ fontSize: "16px", marginLeft: "4px", color: '#64748b' }}>+</span>}
                                            sx={{
                                                fontSize: "0.82rem",
                                                py: 1.8,
                                                px: 0.5,
                                                borderRadius: '8px',
                                                borderColor: '#cbd5e1',
                                                backgroundColor: '#f8fafc',
                                                color: '#475569',
                                                fontWeight: 500,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: BRAND,
                                                    backgroundColor: `${BRAND}08`,
                                                    color: BRAND,
                                                    transform: 'translateY(-1px)',
                                                }
                                            }}
                                        />
                                    ))
                                }
                                {allBenefits.filter(b => !selectedBenefits.some(sb => sb.id === b.id)).length === 0 && (
                                    <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                        {benefitsInputValue.trim() !== '' ? 'No matching benefits found' : 'No suggestions available'}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Skills */}
                    <Box sx={{ mb: 4 }}>
                        <FormLabel sx={{ ...inputLabelStyle, color: errors.skills ? '#d32f2f' : '#333' }}>Skills *</FormLabel>
                        <TextField
                            id="skills-input"
                            error={!!errors.skills}
                            size="small"
                            placeholder="Search and add skills (e.g. React, JavaScript, Sales)..."
                            value={skillsInputValue}
                            onChange={(e) => setSkillsInputValue(e.target.value)}
                            sx={{ mt: 1, mb: 2, width: '100%', maxWidth: '500px', display: 'block', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                            InputProps={{
                                endAdornment: loadingSkills ? <CircularProgress size={20} /> : null
                            }}
                        />
                        {errors.skills && (
                            <FormHelperText error sx={{ mt: -1.5, mb: 2 }}>
                                {errors.skills}
                            </FormHelperText>
                        )}

                        {/* Selected Skills */}
                        {selectedSkills.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ display: 'block', color: '#6B31C4', fontWeight: 700, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                    Selected Skills ({selectedSkills.length})
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                    {selectedSkills.map((skill) => (
                                        <Chip
                                            key={`selected-${skill.id}`}
                                            label={skill.name || skill.skillName}
                                            onDelete={() => handleSkillToggle(skill)}
                                            deleteIcon={<ClearIcon sx={{ fontSize: '14px !important', color: 'white' }} />}
                                            sx={{
                                                fontSize: "0.82rem",
                                                py: 1.8,
                                                px: 0.5,
                                                borderRadius: '8px',
                                                backgroundColor: BRAND,
                                                color: 'white',
                                                fontWeight: 600,
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'rgba(255, 255, 255, 0.7)',
                                                    '&:hover': {
                                                        color: 'white'
                                                    }
                                                },
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: BRAND_DARK,
                                                    transform: 'translateY(-1px)',
                                                }
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {/* Suggested/Search Skills */}
                        <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: '#888', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {skillsInputValue.trim() !== '' ? 'Search Results' : 'Suggested Skills (Popular)'}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {allSkills
                                    .filter(s => !selectedSkills.some(ss => ss.id === s.id))
                                    .slice(0, visibleSkillsLimit)
                                    .map((skill) => (
                                        <Chip
                                            key={skill.id}
                                            label={skill.name || skill.skillName}
                                            onClick={() => handleSkillToggle(skill)}
                                            variant="outlined"
                                            icon={<span style={{ fontSize: "16px", marginLeft: "4px", color: '#64748b' }}>+</span>}
                                            sx={{
                                                fontSize: "0.82rem",
                                                py: 1.8,
                                                px: 0.5,
                                                borderRadius: '8px',
                                                borderColor: '#cbd5e1',
                                                backgroundColor: '#f8fafc',
                                                color: '#475569',
                                                fontWeight: 500,
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    borderColor: BRAND,
                                                    backgroundColor: `${BRAND}08`,
                                                    color: BRAND,
                                                    transform: 'translateY(-1px)',
                                                }
                                            }}
                                        />
                                    ))
                                }
                                {allSkills.filter(s => !selectedSkills.some(ss => ss.id === s.id)).length === 0 && (
                                    <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                        {skillsInputValue.trim() !== '' ? 'No matching skills found' : 'No suggestions available'}
                                    </Typography>
                                )}
                            </Box>
                            {allSkills.filter(s => !selectedSkills.some(ss => ss.id === s.id)).length > 15 && (
                                <Box sx={{ mt: 1.5, display: 'flex', gap: 2 }}>
                                    {allSkills.filter(s => !selectedSkills.some(ss => ss.id === s.id)).length > visibleSkillsLimit ? (
                                        <Button
                                            onClick={() => setVisibleSkillsLimit(prev => prev + 15)}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '0.82rem',
                                                color: BRAND,
                                                fontWeight: 600,
                                                p: 0,
                                                minWidth: 'auto',
                                                '&:hover': { backgroundColor: 'transparent', color: BRAND_DARK, textDecoration: 'underline' }
                                            }}
                                        >
                                            + View More
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => setVisibleSkillsLimit(15)}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '0.82rem',
                                                color: BRAND,
                                                fontWeight: 600,
                                                p: 0,
                                                minWidth: 'auto',
                                                '&:hover': { backgroundColor: 'transparent', color: BRAND_DARK, textDecoration: 'underline' }
                                            }}
                                        >
                                            - View Less
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>

                    {/* Job Description */}
                    <Box sx={{ mb: 4 }}>
                        <FormLabel sx={{ ...inputLabelStyle, color: errors.description ? '#d32f2f' : '#333' }}>Job description *</FormLabel>
                        <Box sx={{ border: `1px solid ${errors.description ? '#d32f2f' : '#ddd'}`, borderRadius: 2, overflow: 'hidden', backgroundColor: "white", mt: 1 }}>
                            
                            {/* ContentEditable div */}
                            <Box
                                id="job-description-editor"
                                contentEditable
                                suppressContentEditableWarning
                                onInput={handleDescriptionChange}
                                ref={setEditorRef}
                                sx={{
                                    p: 2,
                                    minHeight: "220px",
                                    maxHeight: "350px",
                                    overflowY: "auto",
                                    fontSize: "0.95rem",
                                    outline: "none",
                                    lineHeight: 1.6,
                                    color: '#444',
                                    "&:empty:before": {
                                        content: '"Enter job description..."',
                                        color: "#999",
                                    },
                                    "& p": { margin: 0 },
                                    "& ul, & ol": { marginTop: 0, marginBottom: 0, paddingLeft: "20px" },
                                }}
                            />
                            
                            {/* Toolbar (Bottom) */}
                            <Box sx={{ p: 1, display: "flex", gap: 0.5, alignItems: "center", borderTop: '1px solid #eee', bgcolor: '#fafafa' }}>
                                <IconButton size="small" onClick={handleBold} sx={{ color: "#666" }}>
                                    <FormatBoldIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleItalic} sx={{ color: "#666" }}>
                                    <FormatItalicIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleUnderline} sx={{ color: "#666" }}>
                                    <FormatUnderlinedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleStrikethrough} sx={{ color: "#666" }}>
                                    <Typography sx={{ fontSize: "14px", fontWeight: 600, textDecoration: "line-through" }}>S</Typography>
                                </IconButton>
                                <IconButton size="small" onClick={handleBulletList} sx={{ color: "#666" }}>
                                    <FormatListBulletedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleNumberList} sx={{ color: "#666" }}>
                                    <FormatListNumberedIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleSubscript} sx={{ color: "#666" }}>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>T<sub>2</sub></Typography>
                                </IconButton>
                                <IconButton size="small" onClick={handleSuperscript} sx={{ color: "#666" }}>
                                    <Typography sx={{ fontSize: "13px", fontWeight: 600 }}>T<sup>2</sup></Typography>
                                </IconButton>
                                <IconButton size="small" onClick={handleAlignLeft} sx={{ color: "#666" }}>
                                    <FormatAlignLeftIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleAlignCenter} sx={{ color: "#666" }}>
                                    <FormatAlignCenterIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" onClick={handleAlignRight} sx={{ color: "#666" }}>
                                    <FormatAlignRightIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                        {errors.description && (
                            <FormHelperText error sx={{ mt: 0.5 }}>
                                {errors.description}
                            </FormHelperText>
                        )}
                    </Box>

                    {/* Alerts */}
                    {(submitError || submitSuccess) && (
                        <Box sx={{ mb: 3 }}>
                            {submitError && <Alert severity="error" onClose={() => setSubmitError("")}>{submitError}</Alert>}
                            {submitSuccess && <Alert severity="success" onClose={() => setSubmitSuccess("")}>{submitSuccess}</Alert>}
                        </Box>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => setFormData(initialFormState)}
                            sx={{
                                px: 4, py: 1,
                                borderColor: BRAND,
                                color: BRAND,
                                borderRadius: "100px",
                                fontWeight: 600,
                                textTransform: 'none',
                                "&:hover": { borderColor: BRAND_DARK, color: BRAND_DARK, backgroundColor: 'rgba(107, 49, 196, 0.04)' }
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{
                                px: 5, py: 1,
                                backgroundColor: BRAND,
                                color: '#fff',
                                fontWeight: 600,
                                borderRadius: "100px",
                                textTransform: 'none',
                                "&:hover": { backgroundColor: BRAND_DARK }
                            }}
                        >
                            {isSubmitting ? "Posting..." : "Confirm & Post"}
                        </Button>
                    </Box>

                </Paper>
            </Container>
            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnack(prev => ({ ...prev, open: false }))} severity={snack.severity} sx={{ width: '100%', borderRadius: 2 }}>
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CreateNewJob;